/**
 * reducers/path-variables.ts
 * desc: client path variables
 * note: connects to third-party systems via Y Meadows API endpoints
 *
 */

import type { Reducer } from '@reduxjs/toolkit';
import { sortAz } from "../../utilities";
import type { PathVariablesAction } from '../actions/path-variables';
import type { VariableDefinitionDto } from '../../types/api/variable-definitions';
import type { ComponentInfo } from '../../types/api/components';

export interface PathVariablesState {
    isLoading: {
        createPathVariable: boolean;
        deletePathVariable: boolean;
        getPathVariables: boolean;
        getPathVariable: boolean;
        getPathVariableUsage: boolean;
        updatePathVariable: boolean;
    };
    // an empty object until a variable is created, fetched or updated
    detail: VariableDefinitionDto | Record<string, never>;
    list: VariableDefinitionDto[];
    pathVariableUsage: ComponentInfo[];
}

const INITIAL_STATE: PathVariablesState = {
    isLoading: {
        createPathVariable: false,
        deletePathVariable: false,
        getPathVariables: true,
        getPathVariable: false,
        getPathVariableUsage: false,
        updatePathVariable: false,
    },
    detail: {},
    list: [],
    pathVariableUsage: [],
};


function pathVariablesReducer(state: PathVariablesState = INITIAL_STATE, action: PathVariablesAction): PathVariablesState {
    switch (action.type) {
        // CLEAR Path Variables
        case 'CLEAR_PATH_VARIABLES':
            return _clearPathVariables();

        // CREATE Path Variable (Instance)
        case 'CREATE_PATH_VARIABLE_ERROR':
            return _createPathVariableError(state);
        case 'CREATE_PATH_VARIABLE_REQUEST':
            return _createPathVariableRequest(state);
        case 'CREATE_PATH_VARIABLE_SUCCESS':
            return _createPathVariableSuccess(state, action.payload);

        // DELETE Path Variable (Instance)
        case 'DELETE_PATH_VARIABLE_ERROR':
            return _deletePathVariableError(state);
        case 'DELETE_PATH_VARIABLE_REQUEST':
            return _deletePathVariableRequest(state);
        case 'DELETE_PATH_VARIABLE_SUCCESS':
            return _deletePathVariableSuccess(state, action.payload);

        // GET Path Variables (Collection)
        case 'GET_PATH_VARIABLES_ERROR':
        case 'GET_PATH_VERSION_VARIABLES_ERROR':
            return _getPathVariablesError(state);
        case 'GET_PATH_VARIABLES_REQUEST':
        case 'GET_PATH_VERSION_VARIABLES_REQUEST':
            return _getPathVariablesRequest(state);
        case 'GET_PATH_VARIABLES_SUCCESS':
        case 'GET_PATH_VERSION_VARIABLES_SUCCESS':
            return _getPathVariablesSuccess(state, action.payload);

        // GET Path Variable (Instance)
        case 'GET_PATH_VARIABLE_ERROR':
            return _getPathVariableError(state);
        case 'GET_PATH_VARIABLE_REQUEST':
            return _getPathVariableRequest(state);
        case 'GET_PATH_VARIABLE_SUCCESS':
            return _getPathVariableSuccess(state, action.payload);

        // GET Path Variable Usage (Instance)
        case 'GET_PATH_VARIABLE_USAGE_ERROR':
            return _getPathVariableUsageError(state);
        case 'GET_PATH_VARIABLE_USAGE_REQUEST':
            return _getPathVariableUsageRequest(state);
        case 'GET_PATH_VARIABLE_USAGE_SUCCESS':
            return _getPathVariableUsageSuccess(state, action.payload);

        // Update Path Variable (Instance)
        case 'UPDATE_PATH_VARIABLE_ERROR':
            return _updatePathVariableError(state);
        case 'UPDATE_PATH_VARIABLE_REQUEST':
            return _updatePathVariableRequest(state);
        case 'UPDATE_PATH_VARIABLE_SUCCESS':
            return _updatePathVariableSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Path Variables       **************
//*************************************************
function _clearPathVariables(): PathVariablesState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CREATE Path Variable (Instance)   *******
//*************************************************
function _createPathVariableError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVariable: false,
        },
    };
}

function _createPathVariableRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVariable: true,
        },
    };
}

function _createPathVariableSuccess(state: PathVariablesState, payload: VariableDefinitionDto): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createPathVariable: false,
        },
        detail: payload,
        list: sortAz([ ...state.list, payload ], 'name'),
    };
}



//*************************************************
//*****   DELETE Path Variable (Instance)   *******
//*************************************************
function _deletePathVariableError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePathVariable: false,
        },
    };
}

function _deletePathVariableRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePathVariable: true,
        },
    };
}

function _deletePathVariableSuccess(state: PathVariablesState, variableId: string): PathVariablesState {
    // create new list without deleted path variable
    const [ ...pathVariablesList ] = state.list;
    const deletedIndex = pathVariablesList.findIndex(x => x.id === variableId);
    if (deletedIndex > -1) pathVariablesList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deletePathVariable: false,
        },
        list: sortAz(pathVariablesList, 'name'),
    };
}



//*************************************************
//*****   GET Path Variables (Collection)   *******
//*************************************************
function _getPathVariablesError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariables: false,
        },
    };
}

function _getPathVariablesRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariables: true,
        },
    };
}

function _getPathVariablesSuccess(state: PathVariablesState, payload: VariableDefinitionDto[]): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariables: false,
        },
        list: sortAz(payload, 'name'),
    };
}



//*************************************************
//*****   GET Path Variable (Instance)   **********
//*************************************************
function _getPathVariableError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariable: false,
        },
    };
}

function _getPathVariableRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariable: true,
        },
    };
}

function _getPathVariableSuccess(state: PathVariablesState, payload: VariableDefinitionDto): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariable: false,
        },
        detail: payload,
    };
}



//*************************************************
//*****   GET Path Variable Usage (Instance)   ****
//*************************************************
function _getPathVariableUsageError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariableUsage: false,
        },
    };
}

function _getPathVariableUsageRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariableUsage: true,
        },
    };
}

function _getPathVariableUsageSuccess(state: PathVariablesState, payload: ComponentInfo[]): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getPathVariableUsage: false,
        },
        pathVariableUsage: payload,
    };
}



//*************************************************
//*****   UPDATE Path Variable (Instance)     *****
//*************************************************
function _updatePathVariableError(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePathVariable: false,
        },
    };
}

function _updatePathVariableRequest(state: PathVariablesState): PathVariablesState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePathVariable: true,
        },
    };
}

function _updatePathVariableSuccess(state: PathVariablesState, payload: VariableDefinitionDto): PathVariablesState {
    let pathVariablesList = [ ...state.list ];

    // if list exists, modify w updated path variable
    // note: list will not exist if deeplinked or refreshed detail page
    if (pathVariablesList.length) {
        const updatedIndex = pathVariablesList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) pathVariablesList[updatedIndex] = payload;
        pathVariablesList = sortAz(pathVariablesList, 'name');
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updatePathVariable: false,
        },
        detail: payload,
        list: pathVariablesList,
    };
}

export default pathVariablesReducer as Reducer<PathVariablesState>;
